import { Fragment, useCallback } from "react";

import { COMMODITY_NAME_COTTON } from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { round_ex } from "@datanac/datanac-api-toolkit/dist/utility/Math";
import { useColorScheme, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { calculateWeightedValue } from "../../../api-toolkit/useArcPlc";
import { formatPercentage } from "../../../utility/formatters";
import { getCellColor, getHeaderColor } from "../../StressTestVisualization/getCellColor";

export function ArcCoStressTestGrid({
  title,
  commodity_name,

  insurance_plan_uuid,
  displayFieldName,

  xAxisFieldName,
  xAxisDisplayName,

  yAxisFieldName,
  yAxisDisplayName,

  doMarkNegativeValues,

  stressTestResults,
  stressTestResultsIrrigated,
  stressTestResultsNonIrrigated,

  maxLossValue,

  nonIrrigatedScaleFactor,
  historicalIrrigationPercentage,

  // New props for display mode functionality
  displayMode,
  budgetTotalReportedAcreage,
  arcPlcTotalBaseAcreage,

  // Optional prop for highlighting specific row/column
  highlightRowIndex,
}) {
  const isXXL = useMediaQuery("(min-width:1537px)");
  const { mode } = useColorScheme();

  const getDisplayValue = useCallback(
    (cell, insurance_plan_uuid, displayFieldName) => {
      let displayValue = null;

      // Calculate weighted value on-demand
      displayValue =
        calculateWeightedValue(
          cell.row_index,
          cell.column_index,
          displayFieldName,
          insurance_plan_uuid,
          stressTestResults,
          stressTestResultsIrrigated,
          stressTestResultsNonIrrigated
        ) || 0;

      return displayValue;
    },
    [stressTestResultsIrrigated, stressTestResultsNonIrrigated, stressTestResults]
  );

  const getXAxis = () => {
    if (stressTestResults) {
      const xAxisElements = stressTestResults[0]?.map((row) => {
        return row[xAxisFieldName || "yield"];
      });

      return xAxisElements;
    }
  };

  // TODO maybe replace line 48-68 it with utility/formatters
  const _priceFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencySign: "accounting",
    maximumFractionDigits: 0,
  });
  const _priceFormatter2 = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencySign: "accounting",
    maximumFractionDigits: 2,
  });

  const formatPrice = (value) => {
    return value || value === 0 ? _priceFormatter.format(value)?.replace("$", "$ ") : "";
  };

  const formatPrice2 = (value) => {
    return value || value === 0 ? _priceFormatter2.format(value)?.replace("$", "$ ") : "";
  };

  const maxProfitValue =
    stressTestResults && stressTestResults?.length && stressTestResults[0]?.length
      ? getDisplayValue(stressTestResults[0][0], insurance_plan_uuid, displayFieldName)
      : 1000;

  const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      enterDelay={1000}
      enterTouchDelay={1000}
      enterNextDelay={1000}
    />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: "none",
    },
  });

  const filterCotton = (cell, commodity_name) => {
    return commodity_name !== COMMODITY_NAME_COTTON
      ? {
          ...cell,
          yield_seedcotton: undefined,
          county_yield_seedcotton: undefined,
        }
      : cell;
  };

  return (
    <>
      <TableContainer sx={{ boxSizing: "border-box" }} className="ArcCoDataGrid">
        {title && (
          <Box sx={{ p: 1 }}>
            <Typography>{title}</Typography>
          </Box>
        )}
        <Table
          data-testid="ARC-stress-test"
          padding="none"
          spacing={0}
          size="small"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              fontSize: {
                xs: "0.75rem",
                sm: "0.875rem",
              },
            },
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={1}
                sx={{
                  textAlign: "center",
                  padding: "0 !important",
                  paddingTop: "4pt !important",
                  paddingBottom: "4pt !important",
                  paddingRight: "4pt !important",
                  paddingLeft: "4pt !important",
                  backgroundColor: "background.paper",
                  borderRadius: "8px 0 0 0",
                }}
              >
                {yAxisDisplayName || "Price"}
              </TableCell>
              <TableCell
                colSpan="10"
                sx={{
                  textAlign: "center",
                  backgroundColor: "background.paper",
                  borderRadius: "0 8px 0 0",
                }}
              >
                {xAxisDisplayName || "County Yield"}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                borderBottom: "1px solid",
                borderColor: "colors.borderLight",
              }}
            >
              <TableCell
                sx={{
                  padding: "0 !important",
                  backgroundColor: "background.paper",
                }}
              >
                &nbsp;
              </TableCell>
              {(() => {
                const xAxisItems = getXAxis();
                const totalXItems = xAxisItems?.length || 0;
                return xAxisItems?.map((x, index) => {
                  // Get the cell data for this column to show yields in tooltip
                  let cellForColumn = filterCotton(stressTestResults?.[0]?.[index], commodity_name);
                  return (
                    <CustomWidthTooltip
                      key={`x-axis-tooltip-${index}`}
                      arrow
                      title={
                        cellForColumn ? (
                          <>
                            <Typography>
                              <strong>Yields for this column:</strong>
                              <br />
                              &nbsp;&nbsp;Yield: {cellForColumn["yield"]}
                              {Boolean(cellForColumn["yield_seedcotton"]) && (
                                <>
                                  <br />
                                  &nbsp;&nbsp;Yield (Seed Cotton): {cellForColumn["yield_seedcotton"]}
                                </>
                              )}
                              <br />
                              <br />
                              <strong>County Yields:</strong>
                              <br />
                              &nbsp;&nbsp;County Yield: {cellForColumn["county_yield"]}
                              {Boolean(cellForColumn["county_yield_seedcotton"]) && (
                                <>
                                  <br />
                                  &nbsp;&nbsp;County Yield (Seed Cotton): {cellForColumn["county_yield_seedcotton"]}
                                </>
                              )}
                              <br />
                            </Typography>
                          </>
                        ) : null
                      }
                    >
                      <TableCell
                        key={index}
                        sx={{
                          textAlign: "right",
                          padding: "0 !important",
                          paddingTop: "4pt !important",
                          paddingBottom: "4pt !important",
                          paddingRight: "4pt !important",
                          borderBottom: "1px solid var(--mui-palette-grey-) !important",
                          backgroundColor: getHeaderColor(index, totalXItems, mode !== "light"),
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.75rem",
                          },
                        }}
                      >
                        {x}
                      </TableCell>
                    </CustomWidthTooltip>
                  );
                });
              })()}
            </TableRow>

            {stressTestResults &&
              stressTestResults?.length > 0 &&
              stressTestResults[0] &&
              stressTestResults[0].length > 0 &&
              stressTestResults[0][0] &&
              stressTestResults[0][0].price !== undefined &&
              stressTestResults
                ?.filter((row, index) => {
                  // Add safety checks for the filter condition
                  const firstCell = stressTestResults[0] && stressTestResults[0][0];
                  const lastRow = stressTestResults[stressTestResults.length - 1];
                  const lastCell = lastRow && lastRow[lastRow.length - 1];

                  if (!firstCell || !lastCell || firstCell.price === undefined || lastCell.price === undefined) {
                    return index === 0; // Default to showing first row only if we can't determine price range
                  }

                  return firstCell.price !== lastCell.price ? true : index === 0 ? true : false;
                })
                .map((row, rowIndex) => {
                  const filteredResults = stressTestResults?.filter((row, index) => {
                    // Add safety checks for the filter condition
                    const firstCell = stressTestResults[0] && stressTestResults[0][0];
                    const lastRow = stressTestResults[stressTestResults.length - 1];
                    const lastCell = lastRow && lastRow[lastRow.length - 1];

                    if (!firstCell || !lastCell || firstCell.price === undefined || lastCell.price === undefined) {
                      return index === 0; // Default to showing first row only if we can't determine price range
                    }

                    return firstCell.price !== lastCell.price ? true : index === 0 ? true : false;
                  });
                  const totalYItems = filteredResults?.length || 0;
                  const isLastRow = rowIndex === totalYItems - 1;

                  return (
                    <TableRow data-testid={`arc-co-stress-test-row-${rowIndex}`} key={rowIndex}>
                      <TableCell
                        sx={{
                          borderRight: "1px solid",
                          borderColor: "colors.borderLight",
                          p: 0,
                          textAlign: "center",
                          paddingTop: "0 !important",
                          paddingBottom: "0 !important",
                          paddingLeft: "8px !important",
                          paddingRight: "8px !important",
                          maxWidth: { xs: "3.75rem", md: "2.5rem" },
                          backgroundColor: getHeaderColor(rowIndex, totalYItems, mode !== "light"),
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.875rem",
                          },
                          borderRadius: isLastRow ? "0 0 0 8px" : "0",
                        }}
                      >
                        {yAxisFieldName === "price" ? formatPrice2(row[0]?.[yAxisFieldName]) : row[0]?.[yAxisFieldName]}
                      </TableCell>
                      {row.map((cell, index) => {
                        const resultsIrrigated =
                          stressTestResultsIrrigated?.length && stressTestResultsIrrigated[rowIndex][index];
                        const resultsNonIrrigated =
                          stressTestResultsNonIrrigated?.length && stressTestResultsNonIrrigated[rowIndex][index];
                        const indemnityResultsIrrigated =
                          stressTestResultsIrrigated?.length &&
                          stressTestResultsIrrigated[rowIndex][index].indemnityResults[0];
                        const indemnityResultsNonIrrigated =
                          stressTestResultsNonIrrigated?.length &&
                          stressTestResultsNonIrrigated[rowIndex][index].indemnityResults[0];
                        cell = filterCotton(cell, commodity_name);

                        return (
                          <CustomWidthTooltip
                            key={"tooltip" + index}
                            sx={{ maxWidth: 1000 }}
                            arrow
                            title={
                              <>
                                <Typography>
                                  <strong>Yields:</strong>
                                  <br />
                                  &nbsp;&nbsp;Yield: {cell["yield"]}
                                  {Boolean(cell["yield_seedcotton"]) && (
                                    <>
                                      <br />
                                      &nbsp;&nbsp;Yield (Seed Cotton): {cell["yield_seedcotton"]}
                                    </>
                                  )}
                                  <br />
                                  <br />
                                  <strong>County Yields:</strong>
                                  <br />
                                  {Boolean(nonIrrigatedScaleFactor) && (
                                    <>
                                      &nbsp;&nbsp;County Yield (IRR): {round_ex(cell["county_yield"], 0)}
                                      <br />
                                      &nbsp;&nbsp;County Yield (NI):{" "}
                                      {round_ex(cell["county_yield"] * nonIrrigatedScaleFactor, 0)}
                                      {Boolean(cell["county_yield_seedcotton"]) && (
                                        <>
                                          <br />
                                          &nbsp;&nbsp;County Yield (Seed Cotton): {cell["county_yield_seedcotton"]}
                                        </>
                                      )}
                                      <br />
                                    </>
                                  )}
                                  {!nonIrrigatedScaleFactor && (
                                    <>
                                      &nbsp;&nbsp;County Yield: {cell["county_yield"]}
                                      {Boolean(cell["county_yield_seedcotton"]) && (
                                        <>
                                          <br />
                                          &nbsp;&nbsp;County Yield (Seed Cotton): {cell["county_yield_seedcotton"]}
                                        </>
                                      )}
                                      <br />
                                    </>
                                  )}
                                  <br />
                                  <br />
                                  <strong>Prices:</strong>
                                  <br />
                                  &nbsp;&nbsp;MYA: {formatPrice2(cell["price"])}
                                  <br />
                                  {Boolean(nonIrrigatedScaleFactor) && (
                                    <>
                                      &nbsp;&nbsp;County Revenue (IRR):{" "}
                                      {formatPrice(cell["price"] * cell["county_yield"])}
                                      <br />
                                      &nbsp;&nbsp;County Revenue (NI):{" "}
                                      {formatPrice(cell["price"] * cell["county_yield"] * nonIrrigatedScaleFactor)}
                                    </>
                                  )}
                                </Typography>
                                <Divider />
                                <Typography>
                                  {cell.indemnityResults &&
                                    cell.indemnityResults.length > 0 &&
                                    cell.indemnityResults.map((r, resultsIndex) => {
                                      return (
                                        <Fragment key={resultsIndex}>
                                          ARC County
                                          {r.indemnity_total === 0 && (
                                            <>
                                              <br />
                                              No payment
                                            </>
                                          )}
                                          {r.indemnity_total > 0 && nonIrrigatedScaleFactor !== null && (
                                            <>
                                              <br />
                                              HIP: {formatPercentage(historicalIrrigationPercentage)}
                                              <br />
                                              <br />
                                              <u>Per Base Acre</u>
                                              <br />
                                              IRR:{" "}
                                              {formatPrice2(
                                                r?.indemnity_total_per_base_acre * historicalIrrigationPercentage
                                              )}
                                              <br />
                                              NI:{" "}
                                              {formatPrice2(
                                                indemnityResultsNonIrrigated?.indemnity_total_per_base_acre *
                                                  (1 - historicalIrrigationPercentage)
                                              )}
                                              <br />
                                              <br />
                                              <u>Total</u>
                                              <br />
                                              IRR: {formatPrice2(indemnityResultsIrrigated?.indemnity_total)}
                                              <br />
                                              NI: {formatPrice2(indemnityResultsNonIrrigated?.indemnity_total)}
                                              <br />
                                              Total:{" "}
                                              {formatPrice(
                                                indemnityResultsIrrigated?.indemnity_total +
                                                  indemnityResultsNonIrrigated?.indemnity_total
                                              )}
                                              <br />
                                              <br />
                                              <u>Projected Payment</u>
                                              <br />
                                              Per Base Acre:{" "}
                                              {formatPrice(
                                                (indemnityResultsIrrigated?.indemnity_total +
                                                  indemnityResultsNonIrrigated?.indemnity_total) /
                                                  (resultsIrrigated?.base_acreage + resultsNonIrrigated?.base_acreage)
                                              )}
                                              <br />
                                              Per Planted Acre:{" "}
                                              {formatPrice(
                                                (indemnityResultsIrrigated?.indemnity_total +
                                                  indemnityResultsNonIrrigated?.indemnity_total) /
                                                  (resultsIrrigated?.reported_acreage +
                                                    resultsNonIrrigated?.reported_acreage)
                                              )}
                                            </>
                                          )}
                                          {r.indemnity_total > 0 && nonIrrigatedScaleFactor === null && (
                                            <>
                                              <br />
                                              <br />
                                              Total: {formatPrice(r.indemnity_total)}
                                              <br />
                                              Per Base Acre: {formatPrice(r.indemnity_total_per_base_acre)}
                                              <br />
                                              Per Planted Acre: {formatPrice(r.indemnity_total_per_acre)}
                                              <br />
                                            </>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  <br />
                                </Typography>
                              </>
                            }
                          >
                            {(() => {
                              const displayValue = getDisplayValue(cell, insurance_plan_uuid, displayFieldName);
                              const isLastCell = index === row.length - 1;
                              return (
                                <TableCell
                                  key={"cell" + index}
                                  sx={{
                                    borderLeft:
                                      highlightRowIndex !== null &&
                                      highlightRowIndex !== undefined &&
                                      index === highlightRowIndex
                                        ? "1px solid #00ff00"
                                        : null,
                                    borderRight:
                                      highlightRowIndex !== null &&
                                      highlightRowIndex !== undefined &&
                                      index === highlightRowIndex
                                        ? "1px solid #00ff00"
                                        : null,
                                    borderTop:
                                      highlightRowIndex !== null &&
                                      highlightRowIndex !== undefined &&
                                      rowIndex === highlightRowIndex
                                        ? "1px solid #00ff00"
                                        : null,
                                    borderBottom:
                                      highlightRowIndex !== null &&
                                      highlightRowIndex !== undefined &&
                                      rowIndex === highlightRowIndex
                                        ? "1px solid #00ff00 !important"
                                        : null,
                                    paddingLeft: "4pt !important",
                                    paddingRight: "8pt !important",
                                    paddingTop: "8pt !important",
                                    paddingBottom: "8pt !important",
                                    textAlign: "right",
                                    backgroundColor: getCellColor(
                                      displayValue,
                                      maxLossValue,
                                      doMarkNegativeValues,
                                      maxProfitValue,
                                      false,
                                      mode !== "light"
                                    ),
                                    borderRadius: isLastRow && isLastCell ? "0 0 8px 0" : "0",
                                  }}
                                >
                                  {formatPrice(displayValue) || " "}
                                  <br />
                                </TableCell>
                              );
                            })()}
                          </CustomWidthTooltip>
                        );
                      })}
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
